// ChartRenderer.tsx
import React, {useMemo, useState, useEffect, useRef, Component, ErrorInfo, ReactNode} from "react";
import {
    Chart as ChartJS,
    ChartData,
    ChartOptions,
    ChartType,
    ArcElement,
    TimeScale,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    BarElement,
    BubbleController,
    ScatterController,
    Tooltip,
    Legend,
    Title
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line, Bar, Pie, Doughnut, Radar, PolarArea, Bubble, Scatter } from "react-chartjs-2";

import { parseChartConfig, ChartConfig } from "./../../kgnf_chart_models"
import debounce from "lodash/debounce";

interface ChartRendererProps {
    configJson: string;
}

// Error Boundary for Chart rendering errors
interface ChartErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ChartErrorBoundary extends Component<{ children: ReactNode }, ChartErrorBoundaryState> {
    constructor(props: { children: ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("‹chart_error_boundary›", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    background: "#fff3cd",
                    border: "1px solid #ffc107",
                    borderRadius: 8,
                    padding: 16,
                    margin: "16px auto",
                    maxWidth: 600,
                    fontFamily: "Urbanist, Roboto, sans-serif",
                    fontSize: 14,
                    color: "#856404"
                }}>
                    <strong>Chart could not be rendered</strong>
                    <p style={{ margin: "8px 0 0", fontSize: 12 }}>
                        The chart data may be in an incompatible format.
                    </p>
                </div>
            );
        }
        return this.props.children;
    }
}

ChartJS.register(ArcElement, TimeScale, LineController, LineElement, PointElement, LinearScale, CategoryScale, BarElement, BubbleController, ScatterController, Tooltip, Legend, Title);

const ChartRenderer: React.FC<ChartRendererProps> = ({ configJson }) => {
    const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: 400 });
    const chartRef = useRef<any>(null);

    useEffect(() => {
        const handleResize = debounce(() => {
            try {
                const newWidth = window.innerWidth;
                setDimensions(prev => ({ ...prev, width: newWidth }));

                // Force chart to resize
                if (chartRef.current) chartRef.current.resize();
            } catch (error) {
                console.error("‹errr_resizing_chart_031›", error);
            }
        }, 100);
        try {
            window.addEventListener("resize", handleResize);
        } catch (error) {
            console.error("‹errr_resizing_chart_032›", error);
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, []);


    let html: JSX.Element = <div></div>;

    try {
        const { type, data, options } = useMemo(() => {
            let config = parseChartConfig(configJson);
            let { type, data, options } = config;
            return { type, data, options };
        }, [configJson])

        const chartComponents = useMemo<{ [key in ChartType]: React.FC<any> }>(() => ({
            line: Line,
            bar: Bar,
            pie: Pie,
            doughnut: Doughnut,
            radar: Radar,
            polarArea: PolarArea,
            bubble: Bubble,
            scatter: Scatter
        }), []);

        const ChartComponent = useMemo(() => chartComponents[type], [chartComponents, type]);

        if (!ChartComponent) return <div></div>;
        try{
            import.meta.env.DEV === true && console.log("‹chrt›", data);
        } catch {
            import.meta.env.DEV === true && console.log("‹errr_chrt›");
        }

        try{
            html = (
                <div
                    style={{
                        background: "#fff",
                        borderRadius: 16,
                        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.08)",

                        maxWidth: 1200,
                        minWidth: 320,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontFamily: "Urbanist, Roboto, sans-serif",
                        fontSize: 8
                    }}
                >
                    <ChartComponent
                        data={data}
                        ref={chartRef}
                        options={{
                            ...options,
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                tooltip: {
                                    titleFont: { size: 12, family: "Urbanist, Roboto, sans-serif" },
                                    bodyFont: { size: 12, family: "Urbanist, Roboto, sans-serif" },
                                    footerFont: { size: 12, family: "Urbanist, Roboto, sans-serif" },
                                },
                                legend: {
                                    display: true,
                                    position: "top",
                                    labels: {
                                        font: { size: 11, family: "Urbanist, Roboto, sans-serif" },
                                        color: "#333",
                                        padding: 5
                                    }
                                },
                                title: {
                                    font: { size: 18, family: "Urbanist, Roboto, sans-serif" },
                                    align: "end",
                                },
                                ...((options && (options as any)?.plugins) || {})
                            },

                            layout: {
                                padding: 5,
                                ...((options && (options as any)?.layout) || {})
                            },
                            interaction: {
                                mode: "nearest",
                                intersect: false,
                                ...((options && (options as any)?.interaction) || {})
                            },
                            font: {
                                family: "Urbanist, Roboto, sans-serif",
                                size: 10,
                                weight: "normal",
                                lineHeight: 1.2,
                                style: "normal"
                            }
                        }}
                        height={350}
                    />
                </div>
            );
        } catch {
            import.meta.env.DEV === true && console.log("‹error_rendering_chart_07›");
            return <div></div>;
        }

    } catch (error: any) {
        import.meta.env.DEV === true && console.log("‹error_rendering_chart_02›");
        return <div></div>;
    }

    return <ChartErrorBoundary>{html}</ChartErrorBoundary>;
};

export default ChartRenderer;
